import { PresetTrainingHelper } from 'classes/helpers/preset-training.helper';
import { PlateCanvas, SZ_GREEN_BUFFER_FT } from 'classes/plate-canvas';
import { MachineContext } from 'contexts/machine.context';
import { TrainingContext } from 'contexts/training.context';
import { parseISO } from 'date-fns';
import { PTStep } from 'enums/training.enums';
import {
  CONF_THRESHOLDS_1,
  CONF_THRESHOLDS_2,
  IPTNextStepRequest,
  IPTNextStepResult,
} from 'interfaces/i-preset-training';
import { PlateHelper } from 'lib_ts/classes/plate.helper';
import { ShotRejectionHelper } from 'lib_ts/classes/shot-rejection.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import {
  getConfidenceCriteria,
  getGroupCriteria,
  GroupRejectedReason,
} from 'lib_ts/enums/shot-rejection.enums';
import {
  DELTA_BREAKS_INCHES_FOR_3_6OZ,
  LOW_LIKELIHOOD,
  PRECISE_PT_MODES,
  PRESET_TRAINING_OPTIONS,
  PresetTrainingMode,
  VLOW_LIKELIHOOD,
} from 'lib_ts/enums/training.enums';
import { ITrainingPitch } from 'lib_ts/interfaces/pitches';
import {
  IPresetOption,
  IPresetTrainingSpec,
} from 'lib_ts/interfaces/training/i-preset-training-spec';
import { ITrainedMachineState } from 'lib_ts/interfaces/training/i-trained-machine-state';
import { cloneDeep, first, maxBy, merge } from 'lodash';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

const CONTEXT_NAME = 'PresetTrainingContext';

export interface IPresetTrainingContext {
  step: PTStep;
  readonly setStep: Dispatch<SetStateAction<PTStep>>;

  showFailure: boolean;
  readonly setShowFailure: Dispatch<SetStateAction<boolean>>;

  iAdjustment: number;
  readonly setIAdjustment: Dispatch<SetStateAction<number>>;

  // whether PT mode or specs can be edited (e.g. not while waiting for data)
  disableForms: boolean;

  options: IPresetOption[];

  gradient: boolean;
  readonly setGradient: (m: boolean) => void;

  active: IPresetOption;
  readonly changeMode: (m: PresetTrainingMode) => void;
  readonly updateSpec: (m: Partial<IPresetTrainingSpec>) => void;

  readonly isPitchAdjustable: (m: ITrainingPitch) => boolean;

  readonly getNextStep: (m: IPTNextStepRequest) => IPTNextStepResult;

  // the preset mode for this will be used to draw filled vs outlined checkmarks in the plate and breaks views
  lastTrainedMs: ITrainedMachineState | undefined;
  readonly setLastTrainedMs: (v: ITrainedMachineState | undefined) => void;
}

const DEFAULT: IPresetTrainingContext = {
  step: PTStep.Firing,
  setStep: () => console.error(`${CONTEXT_NAME}: not init`),

  showFailure: false,
  setShowFailure: () => console.error(`${CONTEXT_NAME}: not init`),

  iAdjustment: 0,
  setIAdjustment: () => console.error(`${CONTEXT_NAME}: not init`),

  disableForms: true,
  options: PRESET_TRAINING_OPTIONS,

  gradient: true,
  setGradient: () => console.error(`${CONTEXT_NAME}: not init`),

  active: PRESET_TRAINING_OPTIONS[0],
  changeMode: () => console.error(`${CONTEXT_NAME}: not init`),
  updateSpec: () => console.error(`${CONTEXT_NAME}: not init`),

  isPitchAdjustable: () => false,
  getNextStep: () => {
    console.error(`${CONTEXT_NAME}: not init`);
    return {
      nextStep: PTStep.Sending,
      showFailure: false,
      groupFilterResult: {
        reject: true,
        reasons: [GroupRejectedReason.TooFew],
      },
      reason: 'not init',
    };
  },

  lastTrainedMs: undefined,
  setLastTrainedMs: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const PresetTrainingContext = createContext(DEFAULT);

interface IProps {
  children: ReactNode;
}

export const PresetTrainingProvider: FC<IProps> = (props) => {
  const current = useAuthStore((s) => s.current);

  const r2fReady = useMachineStatusStore((s) => s.r2fReady);

  const { finalMsg } = useContext(TrainingContext);

  const { autoFire, machine, activeModel, checkConfidence } =
    useContext(MachineContext);

  const [lastTrainedMs, setLastTrainedMs] = useState(DEFAULT.lastTrainedMs);

  const [absCanvas] = useState(PlateCanvas.makeAbstract());

  const disableForms = useMemo(() => {
    if (autoFire) {
      return true;
    }

    if (finalMsg) {
      // we've collected data but haven't fired (again) yet
      return false;
    }

    // disable while not R2F
    return !r2fReady;
  }, [autoFire, finalMsg, r2fReady]);

  const [_options, _setOptions] = useState(DEFAULT.options);

  const [_active, _setActive] = useState(() => {
    // see if user has a preference from cookies
    const userPreset = _options.find(
      (p) => p.mode === current.preset_training_mode
    );

    const initPreset: IPresetOption = cloneDeep(
      userPreset ?? DEFAULT.options[0]
    );

    if (initPreset.spec.maxSamples < machine.training_threshold) {
      // in case the sample size is less than the machine threshold
      initPreset.spec.maxSamples = machine.training_threshold;
    }

    if (
      machine.ball_type === BallType.Trajekt_36 &&
      initPreset.spec.deltaBreaksInches < DELTA_BREAKS_INCHES_FOR_3_6OZ
    ) {
      initPreset.spec.deltaBreaksInches = DELTA_BREAKS_INCHES_FOR_3_6OZ;
    }

    return initPreset;
  });

  const [_gradient, _setGradient] = useState(DEFAULT.gradient);

  const changeMode = useCallback(
    (mode: PresetTrainingMode) => {
      // avoid modifiying the original definitions
      const safePreset = cloneDeep(
        _options.find((m) => m.mode === mode) ?? first(_options)
      );

      if (!safePreset) {
        // shouldn't trigger
        return;
      }

      const { training_threshold } = machine;

      if (safePreset.spec.maxSamples < training_threshold) {
        safePreset.spec.maxSamples = training_threshold;
      }

      if (
        machine.ball_type === BallType.Trajekt_36 &&
        safePreset.spec.deltaBreaksInches < 5
      ) {
        safePreset.spec.deltaBreaksInches = 5;
      }

      _setActive(safePreset);
    },
    [_options, machine.training_threshold, machine.ball_type]
  );

  const _updateSpec = useCallback(
    (spec: Partial<IPresetTrainingSpec>) => {
      const nextActive = cloneDeep(_active);
      merge(nextActive.spec, spec);

      // remember any changes for switching between modes
      _setOptions((prev) =>
        prev.map((o) => (o.mode === nextActive.mode ? nextActive : o))
      );

      _setActive(nextActive);
    },
    [_options, _active]
  );

  const _isPitchAdjustable = useCallback(
    (pitch: ITrainingPitch) => {
      if (_active.spec.maxAdjustments === 0) {
        console.warn(
          `Cannot adjust pitch ${pitch._id}: active preset ${_active.mode} has zero max adjustments`
        );
        return false;
      }

      if (!pitch.priority) {
        console.warn(`Cannot adjust pitch ${pitch._id}: no build priority`);
        return false;
      }

      if (!activeModel) {
        console.warn(`Cannot adjust pitch ${pitch._id}: no active model`);
        return false;
      }

      switch (pitch.priority) {
        case BuildPriority.Breaks: {
          return activeModel.supports_breaks;
        }

        case BuildPriority.Spins:
        case BuildPriority.Default:
        default: {
          return activeModel.supports_spins;
        }
      }
    },
    [_active, activeModel]
  );

  const [step, setStep] = useState(DEFAULT.step);
  const [showFailure, setShowFailure] = useState(DEFAULT.showFailure);
  const [iAdjustment, setIAdjustment] = useState(DEFAULT.iAdjustment);

  const state: IPresetTrainingContext = {
    step: step,
    setStep: setStep,

    showFailure: showFailure,
    setShowFailure: setShowFailure,

    iAdjustment: iAdjustment,
    setIAdjustment: setIAdjustment,

    disableForms: disableForms,
    options: _options,

    gradient: _gradient,
    setGradient: _setGradient,

    active: _active,
    changeMode: changeMode,
    updateSpec: _updateSpec,

    isPitchAdjustable: _isPitchAdjustable,

    getNextStep: (config) => {
      const confCriteria = getConfidenceCriteria(config.target.priority);

      const safeShots = checkConfidence
        ? config.shots.filter((s) => {
            const { reject } = ShotRejectionHelper.filterShot({
              shot: s,
              criteria: confCriteria,
            });

            return !reject;
          })
        : config.shots;

      const groupFilterResult = ShotRejectionHelper.filterGroup({
        target: config.target,
        shots: safeShots,
        spec: _active.spec,
        trainingThreshold: machine.training_threshold,
        criteria: getGroupCriteria(config.target.priority),
      });

      console.debug({
        event: 'getNextStep',
        pitch: config.target,
        checkConfidence,
        confCriteria,
        safeShots,
        groupFilterResult,
      });

      if (
        config.iAdjustment > _active.spec.maxAdjustments // no more adjustments possible
      ) {
        const shouldShowFailure = PRECISE_PT_MODES.includes(_active.mode);

        // skip all the math stuff and just make sure we can BP
        if (safeShots.length < machine.training_threshold) {
          const output: IPTNextStepResult = {
            nextStep: PTStep.Sending,
            showFailure: shouldShowFailure,
            reason: `QT: Not enough shots (${safeShots.length} < ${machine.training_threshold})`,
            groupFilterResult: groupFilterResult,
          };

          return output;
        }

        const newestShot = maxBy(safeShots, (m) =>
          parseISO(m._created).getTime()
        );

        if (!newestShot) {
          // shouldn't trigger
          const output: IPTNextStepResult = {
            nextStep: PTStep.Sending,
            showFailure: shouldShowFailure,
            reason: 'QT: Could not find newest shot',
            groupFilterResult: groupFilterResult,
          };

          return output;
        }

        const inZone = PlateHelper.isPlateInZone({
          plateFt: TrajHelper.getPlateLoc(newestShot.traj),
          szDef: absCanvas.CONFIG.zone,
          bufferFt: SZ_GREEN_BUFFER_FT,
        });

        if (!inZone) {
          // keep collecting data until a shot lands in the green zone
          const output: IPTNextStepResult = {
            nextStep: PTStep.Sending,
            showFailure: shouldShowFailure,
            reason: 'QT: Newest shot not in zone',
            groupFilterResult: groupFilterResult,
          };

          return output;
        }

        const output: IPTNextStepResult = {
          nextStep: PTStep.Complete,
          showFailure: shouldShowFailure,
          reason: 'QT: Newest shot in zone',
          groupFilterResult: groupFilterResult,
        };

        return output;
      }

      const maxSamples = Math.max(
        _active.spec.maxSamples,
        machine.training_threshold
      );

      if (!groupFilterResult.reject) {
        const output: IPTNextStepResult = {
          nextStep: PTStep.Complete,
          showFailure: false,
          reason:
            `PT: Enough training shots collected and strictly acceptable ` +
            `(strict: ${groupFilterResult.reasons.join(', ')})`,
          groupFilterResult: groupFilterResult,
        };

        return output;
      }

      // If we have enough shots for BP, check for strict acceptability and return complete as soon as possible
      if (safeShots.length >= maxSamples) {
        const output: IPTNextStepResult = {
          nextStep: PTStep.Iterate,
          showFailure: false,
          reason:
            `PT: Max shots reached but not strictly acceptable ` +
            `(strict: ${groupFilterResult.reasons.join(', ')})`,
          groupFilterResult: groupFilterResult,
        };

        return output;
      }

      if (!_active.spec.smartSampling) {
        const output: IPTNextStepResult = {
          nextStep: PTStep.Sending,
          showFailure: false,
          reason:
            `PT: Not smart sampling ` +
            `(strict: ${groupFilterResult.reasons.join(', ')})`,
          groupFilterResult: groupFilterResult,
        };

        return output;
      }

      // Smart sampling logic
      // 1. Check if we have enough confidence (minimum threshold) to proceed to 2
      // 2. Iterate early if we have very low probability of being acceptable
      // 3. Check if we have enough confidence (more strict threshold) to proceed to 4
      // 4. Iterate early if we have low probability of being acceptable
      // 5. Succeed early if we have high probability of being acceptable and enough shots

      // TODO: Move these hardcorded values elsewhere

      // Minimum confidence required for any early iterations
      const firstConfidenceCheck = PresetTrainingHelper.isEnoughConfidence({
        priority: config.target.priority ?? BuildPriority.Default,
        safeShots: safeShots,
        thresholds: CONF_THRESHOLDS_1,
      });

      if (!firstConfidenceCheck.enough) {
        const output: IPTNextStepResult = {
          nextStep: PTStep.Sending,
          showFailure: false,
          reason:
            `PT: Not enough confidence (first check) for early iteration (${firstConfidenceCheck.reason}) ` +
            `(strict: ${groupFilterResult.reasons.join(', ')})`,
          groupFilterResult: groupFilterResult,
        };

        return output;
      }

      // Very low likelihood check
      // * likelihood is a number or NaN
      const vlh = PresetTrainingHelper.isLowChanceOfAcceptable({
        target: config.target,
        safeShots: safeShots,
        ballType: machine.ball_type,
        spec: _active.spec,
        likelihoodLimit: VLOW_LIKELIHOOD,
      });

      if (vlh.lowLikelihood) {
        const output: IPTNextStepResult = {
          nextStep: PTStep.Iterate,
          showFailure: false,
          reason:
            `PT: Early iteration due to very low likelihood ${vlh.reason} ` +
            `(strict: ${groupFilterResult.reasons.join(', ')})`,
          groupFilterResult: groupFilterResult,
        };

        return output;
      }

      // Minimum confidence required for early iteration with higher probability of acceptable
      const secondConfidenceCheck = PresetTrainingHelper.isEnoughConfidence({
        priority: config.target.priority ?? BuildPriority.Default,
        safeShots: safeShots,
        thresholds: CONF_THRESHOLDS_2,
      });

      if (!secondConfidenceCheck.enough) {
        const output: IPTNextStepResult = {
          nextStep: PTStep.Sending,
          showFailure: false,
          reason:
            `PT: Not enough high confidence (second check) for early iteration (${secondConfidenceCheck.reason}) ` +
            `(strict: ${groupFilterResult.reasons.join(', ')})`,
          groupFilterResult: groupFilterResult,
        };

        return output;
      }

      // Second likelihood check with higher limit
      const lh = PresetTrainingHelper.isLowChanceOfAcceptable({
        target: config.target,
        safeShots: safeShots,
        ballType: machine.ball_type,
        spec: _active.spec,
        likelihoodLimit: LOW_LIKELIHOOD,
      });

      if (lh.lowLikelihood) {
        const output: IPTNextStepResult = {
          nextStep: PTStep.Iterate,
          showFailure: false,
          reason:
            `PT: Early iteration due to low likelihood ${lh.reason} ` +
            `(strict: ${groupFilterResult.reasons.join(', ')})`,
          groupFilterResult: groupFilterResult,
        };

        return output;
      }

      const output: IPTNextStepResult = {
        nextStep: PTStep.Sending,
        showFailure: false,
        reason:
          `PT: Smart sampling not iterating early ${lh.reason} ` +
          `(strict: ${groupFilterResult.reasons.join(', ')})`,
        groupFilterResult: groupFilterResult,
      };

      return output;
    },

    lastTrainedMs: lastTrainedMs,
    setLastTrainedMs: setLastTrainedMs,
  };

  return (
    <PresetTrainingContext.Provider value={state}>
      {props.children}
    </PresetTrainingContext.Provider>
  );
};
